/**
 * FileCard Component
 * Mobile card view for displaying a file with selection checkbox
 */

import { html, attr, on, Signal, computedOf } from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon } from '@tempots/beatui'
import { getFileIcon, getFileIconBg, type FileType } from '@/utils'
import { Workspace } from '@/state/workspace'

// ============================================================================
// Types
// ============================================================================

export interface FileDisplayItem {
  id: string
  hash: string
  name: string
  mimeType: string
  size: string
  createdAt: string
  fileType: FileType
  folderId: string
  checkbox: string
}

export interface FileCardProps {
  file: Signal<FileDisplayItem>
  selectedFileHashes: Signal<Set<string>>
  onToggleSelection: (hash: string) => void
  projectId: Signal<string>
}

// ============================================================================
// Component
// ============================================================================

/**
 * FileCard - Mobile card view for a file
 *
 * @example
 * ```typescript
 * FileCard({
 *   file: fileSignal,
 *   selectedFileHashes,
 *   onToggleSelection: (hash) => toggleFileSelection(hash),
 *   projectId
 * })
 * ```
 */
export function FileCard({
  file,
  selectedFileHashes,
  onToggleSelection,
  projectId,
}: FileCardProps) {
  return html.div(
    attr.class('bg-white border border-gray-200 rounded-lg p-4'),
    html.div(
      attr.class('flex items-start justify-between'),
      html.div(
        attr.class('flex items-center gap-3'),
        // Checkbox
        html.input(
          attr.type('checkbox'),
          attr.class(
            'w-4 h-4 mt-1 text-brand-600 bg-gray-100 border-gray-300 rounded focus:ring-brand-500 cursor-pointer flex-shrink-0'
          ),
          attr.checked(selectedFileHashes.map((s) => s.has(file.get().hash))),
          on.change(() => onToggleSelection(file.get().hash))
        ),
        html.div(
          attr.class(
            file.map(
              (f) =>
                `w-10 h-10 rounded-lg flex items-center justify-center ${getFileIconBg(f.fileType)}`
            )
          ),
          Icon(
            { icon: file.map((f) => getFileIcon(f.fileType)) },
            attr.class('w-5 h-5')
          )
        ),
        html.div(
          html.h3(
            attr.class('font-medium text-gray-900'),
            file.map((f) => f.name)
          ),
          html.p(
            attr.class('text-sm text-gray-500'),
            file.map((f) => f.mimeType)
          )
        )
      ),
      Anchor(
        {
          href: computedOf(
            file,
            projectId
          )((f, pid) => Workspace.projectUrl(`/files/${f.hash}`, pid) ?? '#'),
        },
        attr.class('text-brand-600 hover:text-brand-700'),
        Icon({ icon: 'mdi:chevron-right' }, attr.class('w-5 h-5'))
      )
    ),
    html.div(
      attr.class('mt-3 flex items-center gap-4 text-xs text-gray-500'),
      html.span(file.map((f) => f.size)),
      html.span(
        'Uploaded: ',
        file.map((f) => f.createdAt)
      )
    )
  )
}
